/**
 * Fetch Interceptor
 * Transparently adds Bearer tokens to all internal API requests
 * This allows components to call APIs without manually adding auth headers
 */

let isInterceptorInstalled = false;
let originalFetch: typeof fetch;

/**
 * Setup the fetch interceptor
 * Automatically adds Authorization header with Bearer token to all /api/* requests
 * 
 * Should be called once during app initialization (in AuthProvider)
 */
export function setupFetchInterceptor(): void {
  // Only run in browser
  if (typeof window === "undefined") return;

  // Prevent double installation
  if (isInterceptorInstalled) {
    console.log("[Fetch Interceptor] Already installed");
    return;
  }

  // Save original fetch
  originalFetch = window.fetch;
  isInterceptorInstalled = true;

  console.log("[Fetch Interceptor] Installing interceptor");

  // Override global fetch
  window.fetch = async (input, init) => {
    const url = typeof input === "string"
      ? input
      : input instanceof Request
        ? input.url
        : input.toString();

    // Only intercept internal API routes
    if (url.startsWith("/api/")) {
      const token = localStorage.getItem("dashboard-auth-token");

      if (token) {
        // Add Authorization header
        const headers = new Headers(init?.headers);

        // Only add if not already present
        if (!headers.has("Authorization")) {
          headers.set("Authorization", `Bearer ${token}`);
          console.log("[Fetch Interceptor] Added Bearer token to:", url);
        }

        return originalFetch(input, { ...init, headers });
      }
    }

    // Call original fetch for non-API routes or when no token
    return originalFetch(input, init);
  };
}

/**
 * Restore the original fetch function
 * Useful for cleanup or testing
 */
export function restoreFetch(): void {
  if (typeof window === "undefined") return;

  if (isInterceptorInstalled && originalFetch) {
    window.fetch = originalFetch;
    isInterceptorInstalled = false;
    console.log("[Fetch Interceptor] Removed interceptor");
  }
}

